import { catalog } from '../api/endpoints.ts';
import type { AvailabilityItem, StorefrontItem } from '../api/types.ts';
import { currentStrings, type Strings } from '../i18n/index.ts';
import { currentScope } from './address-book.ts';
import { addToCart, applyAvailability, applyCartChanges, cartStore, type CartChange } from './cart.ts';
import { ageVerdict, askAddress, askAge } from './gates.ts';
import { notify } from './toasts.ts';

/**
 * Добавление в корзину со сверкой наличия.
 *
 * Товар кладётся сразу: ждать ответа сервера человеку незачем. Следом приходит правда о
 * складе: если стало меньше, количество подрезается и появляется мягкая подсказка. Нет связи —
 * ничего не меняется: наличие всё равно проверится при оформлении.
 *
 * Первый товар спрашивает адрес: склад адреса решает, что на полке, и «сюда не доставляем»
 * должно прозвучать до полной корзины.
 */

/** Подсказка о правке: «Jogurt 500g: ostalo je 2». */
export function describeChange(change: CartChange, strings: Strings = currentStrings()): string {
  return change.kind === 'gone'
    ? `${change.name}: ${strings.itemGone}`
    : `${change.name}: ${strings.itemLeft(change.availableQty)}`;
}

/**
 * Товар 18+ кладётся только совершеннолетнему: без даты рождения сначала поднимается лист
 * подтверждения возраста. Возвращает `true`, если товар лёг в корзину сразу.
 */
export function addWithChecks(item: StorefrontItem): boolean {
  if (currentScope().kind === 'none') {
    askAddress(item);
    return false;
  }
  if (item.ageRestricted) {
    const verdict = ageVerdict();
    if (verdict === 'underage') {
      notify(currentStrings().ageGateUnderage);
      return false;
    }
    if (verdict === 'unknown') {
      askAge(item);
      return false;
    }
  }
  addToCart(item);
  void verifyAndFix([item.id]);
  return true;
}

/**
 * Наличие по списку товаров. Запросы идут партиями: сервер принимает до 200 штук. Сервер
 * отвечает по каждому товару из своего каталога, поэтому товар без ответа в нём не существует
 * (корзина собрана на другом бэкенде, товар удалён) и считается закончившимся.
 */
async function availability(productIds: string[]): Promise<AvailabilityItem[] | null> {
  if (productIds.length === 0) return null;
  const scope = currentScope();
  const result: AvailabilityItem[] = [];
  for (let start = 0; start < productIds.length; start += 200) {
    try {
      result.push(...(await catalog.availability(scope, productIds.slice(start, start + 200))).items);
    } catch {
      return null;
    }
  }
  const answered = new Set(result.map((item) => item.productId));
  const lines = cartStore.get().lines;
  for (const productId of productIds) {
    if (answered.has(productId)) continue;
    const line = lines.find((l) => l.productId === productId);
    if (line) result.push({ productId, isActive: false, availableQty: 0, priceRsd: line.priceRsd });
  }
  return result;
}

/** Сверка с правкой корзины и без подсказок: о правках говорит карточка «Корзина обновлена». */
export async function fixCart(productIds: string[]): Promise<CartChange[] | null> {
  const items = await availability(productIds);
  if (!items) return null;
  const changes = applyAvailability(items);
  applyCartChanges(changes);
  return changes;
}

/** Сверка с подсказками: так работает добавление в корзину. `null` — спросить сервер не удалось. */
export async function verifyAndFix(productIds: string[]): Promise<CartChange[] | null> {
  const changes = await fixCart(productIds);
  if (!changes) return null;
  for (const change of changes) notify(describeChange(change));
  return changes;
}

/** Сверка без подсказок: корзина узнаёт остатки, а говорят о них сами строки. */
export async function checkCart(productIds: string[]): Promise<CartChange[] | null> {
  const items = await availability(productIds);
  return items ? applyAvailability(items) : null;
}

/** Свежие остаток и цена одного товара: карточка держит их живыми, пока открыта. */
export async function availabilityOf(productId: string): Promise<AvailabilityItem | null> {
  const items = await availability([productId]);
  if (!items) return null;
  applyAvailability(items);
  return items[0] ?? null;
}
